import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  CALENDAR_LINK_QUERY_KEY,
  type CalendarLink,
  clearCalendarLink,
  readCalendarLink,
  writeCalendarLink,
} from "@/lib/calendar/calendar-link-store";
import {
  calendarWindow,
  removeShiftsFromCalendar,
  syncShiftsToCalendar,
} from "@/lib/calendar/sync-shifts-to-calendar";
import { type Calendar, DeviceCalendar } from "@/lib/capacitor/calendar";
import { useMe } from "@/lib/hooks/use-me";
import { fetchShiftsForCalendar } from "@/lib/query/shift/fetch-shifts-for-calendar";
import { logger } from "@/logger";
import { formatInTz } from "@/utils/format-datetime";

const PRIMARY_BUTTON =
  "touch-target inline-flex items-center justify-center rounded-lg bg-brand-500 px-4 text-sm font-medium text-white disabled:opacity-50";
const SECONDARY_BUTTON =
  "touch-target inline-flex items-center justify-center rounded-lg border border-bordercolor-primary px-4 text-sm font-medium text-txt-primary disabled:opacity-50";

/**
 * De agenda op het toestel zelf: kies er één, en de diensten komen er als echte afspraken in.
 *
 * Dit is de enige weg die op Android werkt — daar kun je nergens een agenda via een URL toevoegen —
 * en de enige die meteen bijwerkt in plaats van "meestal binnen een uur".
 */
export function NativeCalendarLink({ hasSubscription }: { hasSubscription: boolean }) {
  const { t } = useTranslation();
  const { me } = useMe();
  const queryClient = useQueryClient();

  const [calendars, setCalendars] = useState<Calendar[] | null>(null);
  const [accessDenied, setAccessDenied] = useState(false);

  const employeeId = me?.employee?.id;

  const { data: link } = useQuery({
    queryKey: CALENDAR_LINK_QUERY_KEY,
    queryFn: readCalendarLink,
    enabled: DeviceCalendar.isAvailable(),
  });

  async function sync(target: CalendarLink) {
    if (!employeeId) {
      return;
    }
    const shifts = await fetchShiftsForCalendar(employeeId, calendarWindow());
    await syncShiftsToCalendar({ shifts, link: target });
    await queryClient.invalidateQueries({ queryKey: CALENDAR_LINK_QUERY_KEY });
  }

  const chooseMutation = useMutation({
    mutationFn: async () => {
      const granted = (await DeviceCalendar.hasAccess()) || (await DeviceCalendar.requestAccess());
      if (!granted) {
        return null;
      }
      return await DeviceCalendar.listWritableCalendars();
    },
    onSuccess: (result) => {
      setAccessDenied(result === null);
      setCalendars(result);
    },
    onError: (error) => {
      logger.error("Agenda's ophalen mislukt", { error });
      toast.error(t("settings.calendar.native.error"));
    },
  });

  const linkMutation = useMutation({
    mutationFn: async (calendar: Calendar) => {
      const target: CalendarLink = {
        calendarId: calendar.id,
        calendarTitle: calendar.title,
        lastSyncedAt: null,
      };
      await writeCalendarLink(target);
      await sync(target);
    },
    onSuccess: () => {
      setCalendars(null);
      toast.success(t("settings.calendar.native.synced"));
    },
    onError: (error) => {
      logger.error("Agenda koppelen mislukt", { error });
      toast.error(t("settings.calendar.native.error"));
    },
  });

  const syncMutation = useMutation({
    mutationFn: async () => {
      if (link) {
        await sync(link);
      }
    },
    onSuccess: () => toast.success(t("settings.calendar.native.synced")),
    onError: (error) => {
      logger.error("Agenda bijwerken mislukt", { error });
      toast.error(t("settings.calendar.native.error"));
    },
  });

  const unlinkMutation = useMutation({
    mutationFn: async () => {
      if (link) {
        await removeShiftsFromCalendar(link);
      }
      await clearCalendarLink();
      await queryClient.invalidateQueries({ queryKey: CALENDAR_LINK_QUERY_KEY });
    },
    onSuccess: () => toast.success(t("settings.calendar.native.unlinked")),
    onError: (error) => {
      logger.error("Agenda loskoppelen mislukt", { error });
      toast.error(t("settings.calendar.native.error"));
    },
  });

  // In de browser is er geen agenda van een toestel; daar blijft alleen het abonnement over.
  if (!DeviceCalendar.isAvailable()) {
    return null;
  }

  // Zonder medewerker-id valt er niets op te halen; koppelen zou dan een lege agenda opleveren
  // met een bevestiging erbij.
  const isBusy =
    !employeeId ||
    chooseMutation.isPending ||
    linkMutation.isPending ||
    syncMutation.isPending ||
    unlinkMutation.isPending;

  if (link) {
    return (
      <div className="flex flex-col gap-3 rounded-xl border border-bordercolor-primary p-4">
        <div>
          <h2 className="text-base font-semibold text-txt-primary">
            {t("settings.calendar.native.linked", { calendar: link.calendarTitle })}
          </h2>
          {/* Geen project om een tijdzone aan te ontlenen, dus Amsterdam — hetzelfde als overal
              elders in de app. `toLocaleString()` stond hier zonder zone en las dus de klok van
              het toestel. */}
          <p className="mt-1 text-sm text-txt-secondary">
            {link.lastSyncedAt
              ? t("settings.calendar.native.last-synced", {
                  time: formatInTz(link.lastSyncedAt, {
                    year: "numeric",
                    month: "numeric",
                    day: "numeric",
                    hour: "numeric",
                    minute: "numeric",
                    second: "numeric",
                  }),
                })
              : t("settings.calendar.native.never-synced")}
          </p>
        </div>

        {/*
          Twee koppelingen tegelijk zet elke dienst twee keer in de agenda. We kunnen niet zien of
          het abonnement daadwerkelijk ergens hangt — alleen dat de link nog geldig is — dus dit
          is een waarschuwing en geen blokkade.
        */}
        {hasSubscription && (
          <p className="text-xs text-warning-120">
            {t("settings.calendar.native.duplicate-warning")}
          </p>
        )}

        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            disabled={isBusy}
            onClick={() => syncMutation.mutate()}
            className={SECONDARY_BUTTON}
          >
            {t("settings.calendar.native.sync-now")}
          </button>
          <button
            type="button"
            disabled={isBusy}
            onClick={() => unlinkMutation.mutate()}
            className="touch-target rounded-lg px-4 text-sm font-medium text-error-120 disabled:opacity-50"
          >
            {t("settings.calendar.native.unlink")}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3 rounded-xl border border-bordercolor-primary p-4">
      <div>
        <h2 className="text-base font-semibold text-txt-primary">
          {t("settings.calendar.native.title")}
        </h2>
        <p className="mt-1 text-sm text-txt-secondary">
          {t("settings.calendar.native.description")}
        </p>
      </div>

      {accessDenied && (
        <p className="text-sm text-txt-secondary">{t("settings.calendar.native.denied")}</p>
      )}

      {calendars ? (
        <div className="flex flex-col gap-2">
          <p className="text-sm font-medium text-txt-primary">
            {t("settings.calendar.native.choose")}
          </p>
          {calendars.length === 0 ? (
            <p className="text-sm text-txt-secondary">{t("settings.calendar.native.none")}</p>
          ) : (
            calendars.map((calendar) => (
              <button
                key={calendar.id}
                type="button"
                disabled={isBusy}
                onClick={() => linkMutation.mutate(calendar)}
                className="touch-target flex items-center gap-3 rounded-lg border border-bordercolor-primary px-4 text-left text-sm text-txt-primary disabled:opacity-50"
              >
                <span
                  aria-hidden
                  className="h-3 w-3 shrink-0 rounded-full"
                  style={{ backgroundColor: calendar.color }}
                />
                <span className="flex flex-col">
                  <span className="font-medium">{calendar.title}</span>
                  {calendar.accountName && (
                    <span className="text-xs text-txt-secondary">{calendar.accountName}</span>
                  )}
                </span>
              </button>
            ))
          )}
          <button
            type="button"
            onClick={() => setCalendars(null)}
            className={SECONDARY_BUTTON}
            disabled={isBusy}
          >
            {t("settings.calendar.native.cancel")}
          </button>
        </div>
      ) : (
        <button
          type="button"
          disabled={isBusy}
          onClick={() => chooseMutation.mutate()}
          className={PRIMARY_BUTTON}
        >
          {t("settings.calendar.native.link")}
        </button>
      )}
    </div>
  );
}
